import React, { useEffect, useState } from "react";
import useApiStable from "../../utils/useApiStable";
import { useAuth } from "../../context/AuthContext";

const TYPE_LABELS = {
  appointment: { label: "Termin", bg: "#e3f2fd", color: "#1565c0" },
  task: { label: "Aufgabe", bg: "#e8f5e9", color: "#2e7d32" },
  booking: { label: "Buchung", bg: "#fff3e0", color: "#e65100" },
  training: { label: "Mannschaftstraining", bg: "#fce4ec", color: "#880e4f" },
  tnbMatch: { label: "TNB Spieltag", bg: "#f3e5f5", color: "#6a1b9a" },
};

function Badge({ type }) {
  const t = TYPE_LABELS[type] || { label: type, bg: "#eee", color: "#333" };
  return (
    <span style={{
      background: t.bg, color: t.color,
      padding: "2px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600,
      whiteSpace: "nowrap", flexShrink: 0,
    }}>
      {t.label}
    </span>
  );
}

function MyBadge() {
  return (
    <span style={{
      background: "#fff9c4", color: "#f57f17",
      padding: "2px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600,
      whiteSpace: "nowrap", flexShrink: 0,
    }}>
      Mein Termin
    </span>
  );
}

function formatDate(dt) {
  if (!dt) return "";
  try { return new Date(dt).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return String(dt); }
}

function formatTime(dt) {
  if (!dt) return "";
  try { return new Date(dt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function buildEntries(calendar, personId) {
  const entries = [];

  (calendar.appointments || []).forEach(a => {
    const isMe = personId && (
      a.organizer?.id === personId ||
      (a.participants || []).some(p => p.id === personId)
    );
    entries.push({ type: "appointment", start: a.startTime, end: a.endTime, title: a.title || "Termin", location: a.location, description: a.description, isMe, raw: a });
  });

  (calendar.tasks || []).forEach(t => {
    const isMe = personId && (
      t.responsible?.id === personId ||
      (t.assignees || []).some(p => p.id === personId)
    );
    entries.push({ type: "task", start: t.startTime, end: null, title: t.title || "Aufgabe", description: t.description, isMe, raw: t });
  });

  (calendar.bookings || []).forEach(b => {
    const type = b.bookingType === "TRAINING" ? "training" : "booking";
    entries.push({ type, start: b.startTime, end: b.endTime, title: b.title || "Platzbuchung", location: b.location, isMe: b.personalBooking, raw: b });
  });

  (calendar.tnbMatches || []).forEach(m => {
    entries.push({ type: "tnbMatch", start: m.matchDateTime, end: null, title: `${m.homeTeam} – ${m.guestTeam}`, location: m.venue, description: m.league + (m.division ? ` · ${m.division}` : ""), isMe: false, uuid: m.uuid, raw: m });
  });

  entries.sort((a, b) => new Date(a.start) - new Date(b.start));
  return entries;
}

function groupByDate(entries) {
  const groups = [];
  let currentDate = null;
  let currentGroup = null;
  for (const e of entries) {
    const day = e.start ? new Date(e.start).toDateString() : "unbekannt";
    if (day !== currentDate) {
      currentDate = day;
      currentGroup = { date: e.start, entries: [] };
      groups.push(currentGroup);
    }
    currentGroup.entries.push(e);
  }
  return groups;
}

const CalendarView = () => {
  const api = useApiStable();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [person, setPerson] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        if (!user?.username) {
          setError("Bitte melden Sie sich an.");
          setLoading(false);
          return;
        }
        const me = await api.get("/persons/me");
        if (!me?.uuid) {
          setError("Keine Personenzuordnung gefunden.");
          setLoading(false);
          return;
        }
        setPerson(me);
        const data = await api.get(`/calendar/person/${me.uuid}`);
        setEntries(buildEntries(data, me.id));
      } catch (e) {
        setError(e.message || "Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]); // eslint-disable-line

  if (loading) return <div className="container"><p>Lädt Termine…</p></div>;
  if (error) return <div className="container"><p style={{ color: "#c62828" }}>{error}</p></div>;

  const now = new Date();
  const past = entries.filter(e => e.start && new Date(e.start) < now);
  const upcoming = entries.filter(e => !e.start || new Date(e.start) >= now);
  const visible = showPast ? groupByDate(entries) : groupByDate(upcoming);

  const totalCount = entries.length;
  const pastCount = past.length;

  return (
    <div className="container" style={{ maxWidth: 800, margin: "24px auto", padding: "0 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Meine Termine</h2>
        {person && <span style={{ color: "#666", fontSize: 14 }}>{person.name}</span>}
      </div>

      {totalCount === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "#666", padding: 32 }}>
          Keine Termine im Zeitraum.
        </div>
      ) : (
        <>
          {visible.map((group, gi) => (
            <div key={gi} style={{ marginBottom: 24 }}>
              <div style={{
                fontWeight: 700, fontSize: 13, color: "#555", textTransform: "uppercase",
                letterSpacing: 1, padding: "6px 0", borderBottom: "2px solid #e0e0e0", marginBottom: 8,
              }}>
                {formatDate(group.date)}
              </div>
              {group.entries.map((e, i) => (
                <EntryRow key={i} entry={e} />
              ))}
            </div>
          ))}

          {!showPast && pastCount > 0 && (
            <button
              onClick={() => setShowPast(true)}
              style={{
                background: "none", border: "1px solid #ccc", borderRadius: 6,
                padding: "8px 16px", cursor: "pointer", color: "#666", fontSize: 13,
                display: "block", margin: "0 auto",
              }}
            >
              {pastCount} vergangene Termin{pastCount !== 1 ? "e" : ""} anzeigen
            </button>
          )}
        </>
      )}
    </div>
  );
};

function EntryRow({ entry }) {
  const startTime = formatTime(entry.start);
  const endTime = entry.end ? formatTime(entry.end) : null;

  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      padding: "10px 12px", borderRadius: 6, marginBottom: 4,
      background: "#fafafa", borderLeft: `3px solid ${(TYPE_LABELS[entry.type] || {}).color || "#ccc"}`,
    }}>
      <div style={{ minWidth: 72, color: "#555", fontSize: 13, paddingTop: 2, flexShrink: 0 }}>
        {startTime}{endTime && endTime !== startTime ? <>–{endTime}</> : ""}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 2 }}>
          <Badge type={entry.type} />
          {entry.isMe && <MyBadge />}
          <span style={{ fontWeight: 600, fontSize: 14 }}>{entry.title}</span>
        </div>
        {entry.location && (
          <div style={{ color: "#777", fontSize: 13 }}>📍 {entry.location}</div>
        )}
        {entry.description && (
          <div style={{ color: "#888", fontSize: 13, marginTop: 2 }}>{entry.description}</div>
        )}
        {entry.type === "tnbMatch" && entry.uuid && new Date(entry.start) > new Date() && (
          <a
            href={`/api/calendar/ical/tnb-match/${entry.uuid}`}
            download
            style={{ fontSize: 12, color: "#1565c0", textDecoration: "none", marginTop: 4, display: "inline-block" }}
          >
            + Zum Kalender hinzufügen
          </a>
        )}
      </div>
    </div>
  );
}

export default CalendarView;
